import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Home, 
  FileText, 
  Search,
  Image as ImageIcon,
  Video,
  Settings,
  Edit,
  Save,
  X,
  ChevronRight,
  Layers
} from 'lucide-react';
import ContentService from '../../services/contentService';
import axios from 'axios';
import { showToast } from '../ui/Toast';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import Input from '../ui/Input';
import ImageUploader from './ImageUploader';
import './ContentManagerModern.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

const ContentManagerModern = ({ getAuthHeader }) => {
  const [activeTab, setActiveTab] = useState('homepage'); // 'homepage', 'services', 'media'
  const [activeSection, setActiveSection] = useState('home-hero');
  const [selectedService, setSelectedService] = useState('mariages');
  const [editingItemId, setEditingItemId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Utility function to truncate URLs
  const truncateUrl = (url, maxLength = 80) => {
    if (!url || url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  // Check if a field contains a URL
  const isUrlField = (key, value) => {
    if (!value || typeof value !== 'string') return false;
    const urlFields = ['url', 'video_url', 'image_url', 'background_image'];
    return urlFields.includes(key.toLowerCase()) || value.startsWith('http') || value.startsWith('data:');
  };

  // Site Media States
  const [siteMedia, setSiteMedia] = useState([]);
  const [editingMedia, setEditingMedia] = useState(null);

  // Data states
  const [homeServices, setHomeServices] = useState([]);
  const [homeHero, setHomeHero] = useState(null);
  const [homeStats, setHomeStats] = useState([]);
  const [homeQuote, setHomeQuote] = useState(null);
  const [heroSection, setHeroSection] = useState(null);
  const [introSection, setIntroSection] = useState(null);
  const [blocksSection, setBlocksSection] = useState([]);
  const [statsSection, setStatsSection] = useState([]);
  const [processSection, setProcessSection] = useState([]);
  const [ctaSection, setCtaSection] = useState(null);

  const contentService = useMemo(() => new ContentService(getAuthHeader), [getAuthHeader]);

  // Load Site Media
  const loadSiteMedia = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/media`, getAuthHeader());
      setSiteMedia(response.data);
    } catch (error) {
      showToast.error('❌ Erreur lors du chargement des médias');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (activeTab === 'media') {
      loadSiteMedia();
    }
  }, [activeTab, loadSiteMedia]);

  const loadSectionData = useCallback(async () => {
    try {
      setLoading(true);
      switch (activeSection) {
        case 'homepage':
          setHomeServices(await contentService.loadHomeServices());
          break;
        case 'home-hero':
          setHomeHero(await contentService.loadHomeHero());
          break;
        case 'home-stats':
          setHomeStats(await contentService.loadHomeStats());
          break;
        case 'home-quote':
          setHomeQuote(await contentService.loadHomeQuote());
          break;
        case 'service-hero':
          setHeroSection(await contentService.loadServiceHero(selectedService));
          break;
        case 'service-intro':
          setIntroSection(await contentService.loadServiceIntro(selectedService));
          break;
        case 'service-blocks':
          setBlocksSection(await contentService.loadServiceBlocks(selectedService));
          break;
        case 'service-stats':
          setStatsSection(await contentService.loadServiceStats(selectedService));
          break;
        case 'service-process':
          setProcessSection(await contentService.loadServiceProcess(selectedService));
          break;
        case 'service-cta':
          setCtaSection(await contentService.loadServiceCta(selectedService));
          break;
        default:
          break;
      }
    } catch (error) {
      showToast.error('❌ Erreur lors du chargement: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  }, [activeSection, selectedService, contentService]);

  useEffect(() => {
    if (activeTab !== 'media') {
      loadSectionData();
    }
  }, [loadSectionData, activeTab]);

  const handleSave = async (updatedItem) => {
    try {
      const saveMapping = {
        'homepage': () => contentService.updateHomeService(updatedItem.id, {
          title: updatedItem.title,
          description: updatedItem.description,
          icon_name: updatedItem.icon_name,
          image_url: updatedItem.image_url || null
        }),
        'home-hero': () => contentService.updateHomeHero(updatedItem.id, {
          title_line1: updatedItem.title_line1,
          title_line2: updatedItem.title_line2,
          subtitle: updatedItem.subtitle,
          button_text: updatedItem.button_text,
          video_url: updatedItem.video_url
        }),
        'home-stats': () => contentService.updateHomeStat(updatedItem.id, {
          number: updatedItem.number,
          label: updatedItem.label,
          order: updatedItem.order
        }),
        'home-quote': () => contentService.updateHomeQuote(updatedItem.id, {
          title: updatedItem.title,
          subtitle: updatedItem.subtitle,
          button_text: updatedItem.button_text
        }),
        'service-hero': () => contentService.updateServiceHero(updatedItem.id, {
          title_line1: updatedItem.title_line1,
          title_line2: updatedItem.title_line2,
          subtitle: updatedItem.subtitle,
          background_image: updatedItem.background_image
        }),
        'service-intro': () => contentService.updateServiceIntro(updatedItem.id, {
          title: updatedItem.title,
          subtitle: updatedItem.subtitle,
          description: updatedItem.description,
          icon_name: updatedItem.icon_name,
          image_url: updatedItem.image_url || null
        }),
        'service-blocks': () => contentService.updateServiceBlock(updatedItem.id, {
          title: updatedItem.title,
          description: updatedItem.description,
          icon_name: updatedItem.icon_name,
          image_url: updatedItem.image_url || null,
          order: updatedItem.order
        }),
        'service-stats': () => contentService.updateServiceStat(updatedItem.id, {
          number: updatedItem.number,
          label: updatedItem.label,
          order: updatedItem.order
        }),
        'service-process': () => contentService.updateServiceProcess(updatedItem.id, {
          title: updatedItem.title,
          description: updatedItem.description,
          order: updatedItem.order
        }),
        'service-cta': () => contentService.updateServiceCta(updatedItem.id, {
          title: updatedItem.title,
          description: updatedItem.description,
          button_text: updatedItem.button_text
        })
      };

      if (saveMapping[activeSection]) {
        await saveMapping[activeSection]();
        setEditingItemId(null);
        await loadSectionData();
        showToast.success('✅ Modifications enregistrées avec succès !');
      }
    } catch (error) {
      showToast.error('❌ Erreur lors de la sauvegarde: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleMediaSave = async (key) => {
    try {
      await axios.put(`${API}/media/${key}`, editingMedia, getAuthHeader());
      setEditingMedia(null);
      await loadSiteMedia();
      showToast.success('✅ Média mis à jour avec succès !');
    } catch (error) {
      showToast.error('❌ Erreur lors de la sauvegarde du média');
    }
  };

  const getServiceLabel = (type) => {
    const labels = {
      'mariages': 'UN MARIAGE MAGIQUE',
      'communes': 'UNE COMMUNE LUMINEUSE',
      'evenementiel': 'UN ÉVÉNEMENT ÉPOUSTOUFLANT',
      'spectacles': 'SPECTACLES TRANSPORTABLES'
    };
    return labels[type] || type;
  };

  // Filter sections based on search
  const filteredSections = useMemo(() => {
    if (!searchQuery) return null;
    // Simple search implementation
    return null;
  }, [searchQuery]);

  const renderContentSection = () => {
    if (loading) {
      return <div className="content-loading">Chargement...</div>;
    }

    const getCurrentData = () => {
      const sectionMap = {
        'home-hero': [homeHero],
        'home-stats': homeStats,
        'home-quote': [homeQuote],
        'homepage': homeServices,
        'service-hero': [heroSection],
        'service-intro': [introSection],
        'service-blocks': blocksSection,
        'service-stats': statsSection,
        'service-process': processSection,
        'service-cta': [ctaSection]
      };
      return sectionMap[activeSection]?.filter(Boolean) || [];
    };

    const data = getCurrentData();

    return (
      <div className="content-items-grid">
        {data.map((item, index) => (
          <Card key={item.id} className="content-item-card" hover>
            <div className="content-item-header">
              <div>
                <h4>{item.title || item.title_line1 || `Élément ${index + 1}`}</h4>
                {item.subtitle && <p className="content-item-subtitle">{item.subtitle}</p>}
              </div>
              {editingItemId === item.id ? (
                <div className="content-item-actions">
                  <Button
                    variant="primary"
                    size="small"
                    icon={<Save size={16} />}
                    onClick={() => handleSave(item)}
                  >
                    Sauvegarder
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    icon={<X size={16} />}
                    onClick={() => setEditingItemId(null)}
                  >
                    Annuler
                  </Button>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  size="small"
                  icon={<Edit size={16} />}
                  onClick={() => setEditingItemId(item.id)}
                >
                  Modifier
                </Button>
              )}
            </div>
            <div className="content-item-body">
              {editingItemId === item.id ? (
                <div className="content-edit-form">
                  {Object.keys(item).filter(key => !['id', 'updated_at', 'service_type', 'step_number'].includes(key)).map(key => {
                    // Use ImageUploader for image fields
                    if (key === 'image_url' || key === 'background_image') {
                      return (
                        <ImageUploader
                          key={key}
                          currentUrl={item[key] || ''}
                          onUrlChange={(url) => {
                            const newData = getCurrentData().map(d => 
                              d.id === item.id ? { ...d, [key]: url } : d
                            );
                            // Update state based on section
                            if (activeSection === 'home-hero') setHomeHero(newData[0]);
                            else if (activeSection === 'home-stats') setHomeStats(newData);
                            else if (activeSection === 'home-quote') setHomeQuote(newData[0]);
                            else if (activeSection === 'homepage') setHomeServices(newData);
                            else if (activeSection === 'service-hero') setHeroSection(newData[0]);
                            else if (activeSection === 'service-intro') setIntroSection(newData[0]);
                            else if (activeSection === 'service-blocks') setBlocksSection(newData);
                            else if (activeSection === 'service-stats') setStatsSection(newData);
                            else if (activeSection === 'service-process') setProcessSection(newData);
                            else if (activeSection === 'service-cta') setCtaSection(newData[0]);
                          }}
                          getAuthHeader={getAuthHeader}
                          label={key.replace(/_/g, ' ').toUpperCase()}
                          maxSize={10 * 1024 * 1024}
                        />
                      );
                    }
                    
                    // Regular Input for other fields
                    return (
                      <Input
                        key={key}
                        label={key.replace(/_/g, ' ').toUpperCase()}
                        value={item[key] || ''}
                        onChange={(e) => {
                          const newData = getCurrentData().map(d => 
                            d.id === item.id ? { ...d, [key]: e.target.value } : d
                          );
                          // Update state based on section
                          if (activeSection === 'home-hero') setHomeHero(newData[0]);
                          else if (activeSection === 'home-stats') setHomeStats(newData);
                          else if (activeSection === 'home-quote') setHomeQuote(newData[0]);
                          else if (activeSection === 'homepage') setHomeServices(newData);
                          else if (activeSection === 'service-hero') setHeroSection(newData[0]);
                          else if (activeSection === 'service-intro') setIntroSection(newData[0]);
                          else if (activeSection === 'service-blocks') setBlocksSection(newData);
                          else if (activeSection === 'service-stats') setStatsSection(newData);
                          else if (activeSection === 'service-process') setProcessSection(newData);
                          else if (activeSection === 'service-cta') setCtaSection(newData[0]);
                        }}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="content-view">
                  {Object.keys(item).filter(key => !['id', 'updated_at', 'service_type', 'step_number'].includes(key)).map(key => (
                    <div key={key} className="content-field">
                      <span className="content-field-label">{key.replace(/_/g, ' ')}:</span>
                      {isUrlField(key, item[key]) ? (
                        <span 
                          className="content-field-value content-field-url" 
                          title={item[key]}
                        >
                          {truncateUrl(item[key])}
                        </span>
                      ) : (
                        <span className="content-field-value">{item[key] || '-'}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const renderMediaSection = () => {
    const filteredMedia = searchQuery
      ? siteMedia.filter(m => m.key.toLowerCase().includes(searchQuery.toLowerCase()))
      : siteMedia;

    // Separate stats from other media
    const stats = filteredMedia.filter(m => m.type === 'stat');
    const otherMedia = filteredMedia.filter(m => m.type !== 'stat');

    const renderMediaCard = (media) => {
      const isStat = media.type === 'stat';
      
      return (
        <Card key={media.key} className={`media-item-card ${isStat ? 'media-item-card--stat' : ''}`} hover>
          <div className="media-item-header">
            <div className="media-icon">
              {isStat ? (
                <span className="stat-icon">📊</span>
              ) : media.type === 'video' ? (
                <Video size={24} />
              ) : (
                <ImageIcon size={24} />
              )}
            </div>
            <div className="media-info">
              <h4>{media.key}</h4>
              <Badge 
                variant={isStat ? 'warning' : media.type === 'video' ? 'secondary' : 'primary'} 
                size="small"
              >
                {isStat ? 'Statistique' : media.type}
              </Badge>
            </div>
          </div>
          {editingMedia?.key === media.key ? (
            <div className="media-edit-form">
              {isStat ? (
                <>
                  <Input
                    label="Valeur / Chiffre"
                    value={editingMedia.value || ''}
                    onChange={(e) => setEditingMedia({ ...editingMedia, value: e.target.value })}
                    placeholder="300+"
                  />
                  <Input
                    label="Libellé"
                    value={editingMedia.label || ''}
                    onChange={(e) => setEditingMedia({ ...editingMedia, label: e.target.value })}
                    placeholder="Spectacles réalisés"
                  />
                  <Input
                    label="Description (optionnelle)"
                    value={editingMedia.description || ''}
                    onChange={(e) => setEditingMedia({ ...editingMedia, description: e.target.value })}
                    placeholder="Description de la statistique"
                  />
                </>
              ) : media.key?.includes('video') ? (
                <>
                  <Input
                    label="URL Vidéo YouTube"
                    value={editingMedia.url || ''}
                    onChange={(e) => setEditingMedia({ ...editingMedia, url: e.target.value })}
                    placeholder="https://www.youtube.com/embed/..."
                  />
                  <Input
                    label="Description"
                    value={editingMedia.description || ''}
                    onChange={(e) => setEditingMedia({ ...editingMedia, description: e.target.value })}
                    placeholder="Description"
                  />
                </>
              ) : (
                <>
                  <ImageUploader
                    currentUrl={editingMedia.url || ''}
                    onUrlChange={(url) => setEditingMedia({ ...editingMedia, url })}
                    getAuthHeader={getAuthHeader}
                    label="Image"
                    maxSize={10 * 1024 * 1024}
                  />
                  <Input
                    label="Description"
                    value={editingMedia.description || ''}
                    onChange={(e) => setEditingMedia({ ...editingMedia, description: e.target.value })}
                    placeholder="Description"
                  />
                </>
              )}
              <div className="media-actions">
                <Button
                  variant="primary"
                  size="small"
                  icon={<Save size={16} />}
                  onClick={() => handleMediaSave(media.key)}
                >
                  Sauvegarder
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setEditingMedia(null)}
                >
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <div className="media-view">
              {isStat ? (
                <>
                  {media.value && <p className="media-stat-value"><strong>Valeur:</strong> {media.value}</p>}
                  {media.label && <p className="media-stat-label"><strong>Libellé:</strong> {media.label}</p>}
                  {media.description && <p className="media-description"><strong>Description:</strong> {media.description}</p>}
                </>
              ) : (
                <>
                  <p 
                    className="media-url media-url-truncated" 
                    title={media.url}
                  >
                    {truncateUrl(media.url)}
                  </p>
                  {media.description && <p className="media-description">{media.description}</p>}
                </>
              )}
              <Button
                variant="secondary"
                size="small"
                icon={<Edit size={16} />}
                onClick={() => setEditingMedia({ 
                  key: media.key, 
                  url: media.url, 
                  description: media.description,
                  value: media.value,
                  label: media.label
                })}
              >
                Modifier
              </Button>
            </div>
          )}
        </Card>
      );
    };

    return (
      <div className="media-section">
        {stats.length > 0 && (
          <div className="media-subsection">
            <h3 className="media-subsection-title">📊 Statistiques du Site</h3>
            <div className="media-items-grid">
              {stats.map(renderMediaCard)}
            </div>
          </div>
        )}
        
        {otherMedia.length > 0 && (
          <div className="media-subsection">
            <h3 className="media-subsection-title">🎬 Médias (Images & Vidéos)</h3>
            <div className="media-items-grid">
              {otherMedia.map(renderMediaCard)}
            </div>
          </div>
        )}
        
        {filteredMedia.length === 0 && (
          <div className="no-media-message">
            <p>Aucun média trouvé.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="content-manager-modern">
      {/* Header */}
      <div className="content-header">
        <div>
          <h1 className="content-title">Gestion de Contenu & Médias</h1>
          <p className="content-subtitle">
            Modifiez tous les contenus et médias du site depuis une seule interface
          </p>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="content-main-tabs">
        <button
          className={`main-tab ${activeTab === 'homepage' ? 'active' : ''}`}
          onClick={() => { setActiveTab('homepage'); setActiveSection('home-hero'); }}
        >
          <Home size={20} /> Page d'Accueil
        </button>
        <button
          className={`main-tab ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => { setActiveTab('services'); setActiveSection('service-hero'); }}
        >
          <Layers size={20} /> Pages Services
        </button>
        <button
          className={`main-tab ${activeTab === 'media' ? 'active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          <Settings size={20} /> Médias & Stats du Site
        </button>
      </div>

      {/* Search Bar */}
      <Card className="content-search-card">
        <Input
          placeholder="Rechercher une section ou un média..."
          leftIcon={<Search size={18} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Card>

      {/* Section Selector for Homepage/Services */}
      {activeTab !== 'media' && (
        <Card className="section-selector-card">
          {activeTab === 'homepage' && (
            <div className="section-buttons">
              <h4 className="section-group-title">Sections de la page d'accueil</h4>
              <div className="section-buttons-grid">
                <button
                  className={`section-btn ${activeSection === 'home-hero' ? 'active' : ''}`}
                  onClick={() => setActiveSection('home-hero')}
                >
                  <ChevronRight size={16} /> Hero Accueil
                </button>
                <button
                  className={`section-btn ${activeSection === 'home-stats' ? 'active' : ''}`}
                  onClick={() => setActiveSection('home-stats')}
                >
                  <ChevronRight size={16} /> Stats Accueil
                </button>
                <button
                  className={`section-btn ${activeSection === 'homepage' ? 'active' : ''}`}
                  onClick={() => setActiveSection('homepage')}
                >
                  <ChevronRight size={16} /> Services Accueil
                </button>
                <button
                  className={`section-btn ${activeSection === 'home-quote' ? 'active' : ''}`}
                  onClick={() => setActiveSection('home-quote')}
                >
                  <ChevronRight size={16} /> Devis Accueil
                </button>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <>
              <div className="service-selector">
                <h4 className="section-group-title">Sélectionner un service</h4>
                <div className="service-tabs">
                  {['mariages', 'communes', 'evenementiel', 'spectacles'].map((service) => (
                    <button
                      key={service}
                      className={`service-tab ${selectedService === service ? 'active' : ''}`}
                      onClick={() => setSelectedService(service)}
                    >
                      {getServiceLabel(service)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="section-buttons">
                <h4 className="section-group-title">Sections du service</h4>
                <div className="section-buttons-grid">
                  <button
                    className={`section-btn ${activeSection === 'service-hero' ? 'active' : ''}`}
                    onClick={() => setActiveSection('service-hero')}
                  >
                    <ChevronRight size={16} /> Hero
                  </button>
                  <button
                    className={`section-btn ${activeSection === 'service-intro' ? 'active' : ''}`}
                    onClick={() => setActiveSection('service-intro')}
                  >
                    <ChevronRight size={16} /> Introduction
                  </button>
                  <button
                    className={`section-btn ${activeSection === 'service-blocks' ? 'active' : ''}`}
                    onClick={() => setActiveSection('service-blocks')}
                  >
                    <ChevronRight size={16} /> Blocs
                  </button>
                  <button
                    className={`section-btn ${activeSection === 'service-stats' ? 'active' : ''}`}
                    onClick={() => setActiveSection('service-stats')}
                  >
                    <ChevronRight size={16} /> Statistiques
                  </button>
                  <button
                    className={`section-btn ${activeSection === 'service-process' ? 'active' : ''}`}
                    onClick={() => setActiveSection('service-process')}
                  >
                    <ChevronRight size={16} /> Processus
                  </button>
                  <button
                    className={`section-btn ${activeSection === 'service-cta' ? 'active' : ''}`}
                    onClick={() => setActiveSection('service-cta')}
                  >
                    <ChevronRight size={16} /> Appel à l'action
                  </button>
                </div>
              </div>
            </>
          )}
        </Card>
      )}

      {/* Content Display */}
      {activeTab === 'media' ? renderMediaSection() : renderContentSection()}

      {/* Help Section */}
      <Card className="help-card" variant="warning">
        <h4>💡 Guide d'utilisation</h4>
        <ul>
          <li><strong>Page d'Accueil :</strong> Gérez le hero, les stats, les services et la section devis</li>
          <li><strong>Pages Services :</strong> Modifiez le contenu des 4 pages de services (Mariages, Communes, Événementiel, Spectacles)</li>
          <li><strong>Médias & Stats du Site :</strong> Gérez les vidéos, images et statistiques utilisées sur le site</li>
          <li><strong>Changements instantanés :</strong> Les modifications sont appliquées immédiatement</li>
        </ul>
      </Card>
    </div>
  );
};

export default ContentManagerModern;
